import React, { createContext, useContext, useState, useEffect } from 'react';
import io from 'socket.io-client';
import { authAPI, creditsAPI } from '../services/api';

const AuthContext = createContext({
  user: null,
  loading: true,
  isAuthenticated: false,
  socket: null,
  login: () => {},
  register: () => {},
  logout: () => {},
  refreshCredits: () => {},
  refreshUser: () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    console.log('🔐 AuthContext: Checking authentication...');
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      console.log('🔐 AuthContext: Token found?', !!token);
      if (token) {
        // Verify token and get user data
        const response = await authAPI.getMe();

        // Credits bilgisi getMe response'unda zaten var
        const userWithCredits = response.user;
        console.log('🔐 AuthContext: User authenticated with credits from getMe:', userWithCredits.credits);

        setUser({ ...userWithCredits }); // Force re-render with new object reference
        setIsAuthenticated(true);
        console.log('🔐 AuthContext: User authenticated with credits');
      } else {
        setIsAuthenticated(false);
        console.log('🔐 AuthContext: No token, not authenticated');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      console.log('🔐 AuthContext: Auth check complete');
    }
  };

  const login = async (credentials, onSuccess) => {
    try {
      const response = await authAPI.login(credentials);
      const { token } = response;
      localStorage.setItem('auth_token', token);

      // Get fresh user data after login to ensure profile photo and all user info is up to date
      const freshUserResponse = await authAPI.getMe();
      const freshUser = freshUserResponse.user;

      console.log('🔐 AuthContext: User logged in with fresh data, credits:', freshUser.credits);
      console.log('🔐 AuthContext: Profile photo after login:', freshUser.profile_photo);

      setUser({ ...freshUser }); // Force re-render with new object reference
      setIsAuthenticated(true);

      // Initialize socket connection
      //const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:5117', {
      const newSocket = io(import.meta.env.VITE_API_URL?.replace('/api', '') || 'https://api.cleanurphone.xyz', {
        auth: {
          token: token
        }
      });

      newSocket.on('connect', () => {
        console.log('🔌 Socket connected:', newSocket.id);
        newSocket.emit('join', freshUser.id);
      });

      newSocket.on('disconnect', () => {
        console.log('🔌 Socket disconnected');
      });

      setSocket(newSocket);

      // Call success callback after state is updated with longer delay
      // to ensure navbar and other components have time to re-render
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 100);
      }

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token, user } = response;
      localStorage.setItem('auth_token', token);
      setUser(user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Registration failed' };
    }
  };

  const refreshCredits = async () => {
    if (!user?.id) return;

    try {
      const response = await creditsAPI.getBalance();
      if (response.data?.balance !== undefined) {
        setUser(prevUser => ({
          ...prevUser,
          credits: response.data.balance
        }));
        // Dispatch custom event to notify other components of credits change
        window.dispatchEvent(new CustomEvent('creditsUpdated', {
          detail: { credits: response.data.balance }
        }));
        return response.data.balance;
      }
    } catch (error) {
      console.error('Failed to refresh credits:', error);
    }
    return null;
  };

  const refreshUser = async () => {
    try {
      console.log('🔄 Refreshing user data...');
      const response = await authAPI.getMe();
      const freshUser = response.user;
      console.log('🔄 Fresh user data:', freshUser);
      setUser({ ...freshUser }); // Force re-render with new object reference
      return freshUser;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  };

  const logout = async (callback) => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Disconnect socket
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }

      localStorage.removeItem('auth_token');
      setUser(null);
      setIsAuthenticated(false);

      // Execute callback after logout (e.g., navigation)
      if (callback && typeof callback === 'function') {
        callback();
      }
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    socket,
    login,
    register,
    logout,
    refreshCredits,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

